import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  X,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CircleDot,
  Boxes,
  HardDrive,
  Cpu,
  Clock,
  Play,
  Square,
  RotateCcw,
} from 'lucide-react';
import clsx from 'clsx';
import { servers } from '../api';
import type { DockerContainer, DockerHostInfo } from '../api';
import { useDateFormat } from '../lib/dates';

/**
 * Shows what a server's Docker host is actually running.
 *
 * The point is the difference between what we deployed and what is there:
 * containers nobody here created, ones that quietly exited, and how close the
 * disk is to full. On customer-provided hardware that gap is the whole reason to
 * look.
 *
 * Start, stop and restart are available per container. Deliberately nothing that
 * destroys state — no remove, no volume pruning: those belong to the instance
 * lifecycle, which is audited per instance, and a destructive control on a shared
 * overview is an accident waiting to happen.
 */

function stateStyle(state: string, health: string | null): string {
  if (health === 'unhealthy') return 'text-red-600';
  if (state === 'running') return health === 'health: starting' ? 'text-amber-600' : 'text-emerald-600';
  if (state === 'restarting') return 'text-amber-600';
  if (state === 'exited' || state === 'dead') return 'text-red-600';
  return 'text-slate-400';
}

function HostVitals({ host }: { host: DockerHostInfo }) {
  const memPct =
    host.memoryUsedMb && host.memoryTotalMb
      ? Math.round((host.memoryUsedMb / host.memoryTotalMb) * 100)
      : null;

  const items: { icon: typeof Boxes; label: string; value: string; warn?: boolean }[] = [
    {
      icon: Boxes,
      label: 'Container',
      value: `${host.containersRunning ?? '?'} laufen${
        host.containersStopped ? `, ${host.containersStopped} gestoppt` : ''
      }`,
      warn: (host.containersStopped ?? 0) > 0,
    },
    {
      icon: HardDrive,
      label: 'Disk',
      value:
        host.diskUsedPercent !== null
          ? `${host.diskUsedPercent}% belegt${host.diskAvailable ? `, ${host.diskAvailable} frei` : ''}`
          : '—',
      // 85% is where a Docker host starts causing trouble: image pulls fail and
      // Postgres refuses writes long before 100%.
      warn: (host.diskUsedPercent ?? 0) >= 85,
    },
    {
      icon: Cpu,
      label: 'RAM / Load',
      value: [
        memPct !== null ? `${memPct}% (${host.memoryUsedMb}/${host.memoryTotalMb} MB)` : null,
        host.loadAverage,
      ]
        .filter(Boolean)
        .join(' · ') || '—',
      warn: (memPct ?? 0) >= 90,
    },
    {
      icon: Clock,
      label: 'Host',
      value: [host.uptime, host.dockerVersion ? `Docker ${host.dockerVersion}` : null]
        .filter(Boolean)
        .join(' · ') || '—',
    },
  ];

  // Nur zeigen, wenn eine da ist. Eine Kachel „GPU: keine" auf jedem Webserver
  // wäre eine Antwort auf eine Frage, die niemand gestellt hat.
  if (host.gpus?.length) {
    const vram = host.gpus.reduce((sum, g) => sum + (g.vramMb ?? 0), 0);
    items.push({
      icon: Cpu,
      label: 'GPU',
      value: [
        `${host.gpus.length}× ${host.gpus[0].model}`,
        vram ? `${(vram / 1024).toFixed(0)} GB VRAM` : null,
        host.gpuDockerAccess === false ? 'für Docker nicht nutzbar' : null,
      ]
        .filter(Boolean)
        .join(' · '),
      // Eine Karte, die Container nicht erreichen, ist für eine KI-Last so gut
      // wie nicht vorhanden — und das sieht man ihr sonst nicht an.
      warn: host.gpuDockerAccess === false,
    });
  }

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map(({ icon: Icon, label, value, warn }) => (
        <div
          key={label}
          className={clsx(
            'rounded-lg border p-3',
            warn ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-surface',
          )}
        >
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Icon size={13} /> {label}
          </div>
          <div
            className={clsx(
              'mt-1 text-sm font-medium',
              warn ? 'text-amber-800' : 'text-slate-800',
            )}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContainerActions({
  serverId,
  c,
  onDone,
}: {
  serverId: string;
  c: DockerContainer;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = React.useState('');

  const act = useMutation({
    mutationFn: ({ action, force }: { action: 'start' | 'stop' | 'restart'; force?: boolean }) =>
      servers.containerAction(serverId, c.name, action, force),
    onSuccess: (result) => {
      setError(result.ok ? '' : result.error ?? 'Aktion fehlgeschlagen');
      onDone();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      const message = e.response?.data?.message ?? 'Aktion fehlgeschlagen';
      // 409 is the guard on the PC's own containers — ask once, then force.
      if (e.response?.status === 409 && confirm(`${message}

Trotzdem stoppen?`)) {
        act.mutate({ action: 'stop', force: true });
        return;
      }
      setError(message);
    },
  });

  const running = c.state === 'running';
  const busy = act.isPending;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        {running ? (
          <button
            onClick={() => {
              if (confirm(`Container "${c.name}" stoppen?`)) act.mutate({ action: 'stop' });
            }}
            disabled={busy}
            title="Stoppen"
            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Square size={13} />}
          </button>
        ) : (
          <button
            onClick={() => act.mutate({ action: 'start' })}
            disabled={busy}
            title="Starten"
            className="rounded p-1 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-40"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
          </button>
        )}
        <button
          onClick={() => {
            if (confirm(`Container "${c.name}" neu starten?`)) act.mutate({ action: 'restart' });
          }}
          disabled={busy}
          title={t('docker.restart')}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
        >
          <RotateCcw size={13} />
        </button>
      </div>
      {error && <div className="max-w-40 text-[11px] leading-tight text-red-600">{error}</div>}
    </div>
  );
}

function ContainerRow({
  c,
  serverId,
  onDone,
}: {
  c: DockerContainer;
  serverId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-3 py-2">
        <div className="flex items-start gap-2">
          <CircleDot size={12} className={clsx('mt-1 shrink-0', stateStyle(c.state, c.health))} />
          <div className="min-w-0">
            <div className="truncate font-medium text-slate-800">{c.name}</div>
            <div className="truncate font-mono text-[11px] text-slate-400">{c.image}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className={clsx('text-xs font-medium', stateStyle(c.state, c.health))}>{c.status}</div>
        {c.restartCount !== null && c.restartCount > 0 && (
          <div
            className={clsx('text-[11px]', c.restartCount > 3 ? 'text-red-600' : 'text-amber-600')}
            // A container that has restarted repeatedly is usually crash-looping;
            // the status line alone ("Up 2 minutes") hides that entirely.
            title={t('docker.restartsSinceCreation')}
          >
            {c.restartCount}× neu gestartet
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600">
        {c.composeProject ? (
          <>
            <div className="truncate">{c.composeProject}</div>
            {c.composeService && (
              <div className="truncate text-[11px] text-slate-400">{c.composeService}</div>
            )}
          </>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600">
        {c.networks.length ? (
          c.networks.map((n) => (
            <div key={n} className="truncate">
              {n}
            </div>
          ))
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
        {c.ports.length ? (
          c.ports.map((port) => (
            <div key={port} className="truncate">
              {/* A published port on 0.0.0.0 is reachable from the internet —
                  worth seeing at a glance on a machine we do not fully control. */}
              <span className={port.includes('0.0.0.0') ? 'text-amber-700' : ''}>{port}</span>
            </div>
          ))
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {c.managedByPc ? (
          <span className="rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700">PC</span>
        ) : (
          <span className="text-slate-400" title={t('docker.notFromPc')}>
            fremd
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <ContainerActions serverId={serverId} c={c} onDone={onDone} />
      </td>
    </tr>
  );
}

export default function DockerHostPanel({
  serverId,
  hostname,
  onClose,
}: {
  serverId: string;
  hostname: string;
  onClose: () => void;
}) {
  const { relative } = useDateFormat();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['server-docker', serverId],
    queryFn: () => servers.docker(serverId),
    // Each call is an SSH login on someone else's machine — no polling, and keep
    // the result long enough that reopening the panel does not reconnect.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const message =
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (error instanceof Error ? error.message : '');

  const foreign = data?.containers.filter((c) => !c.managedByPc).length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Docker-Host</h2>
            <p className="font-mono text-xs text-slate-500">{hostname}</p>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <span className="text-xs text-slate-400">
                Stand: {relative(data.collectedAt)}
              </span>
            )}
            <button
              onClick={() => void refetch()}
              disabled={isFetching}
              className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isFetching ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              Aktualisieren
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" />
              Verbinde per SSH und frage den Host ab…
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
                <div>
                  <div className="text-sm font-medium text-red-800">
                    Host nicht abfragbar
                  </div>
                  <p className="mt-1 text-sm text-red-700">{message}</p>
                </div>
              </div>
            </div>
          )}

          {data && (
            <>
              <HostVitals host={data.host} />

              {data.warnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
                    <AlertTriangle size={13} /> Teilweise unvollständig
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
                    {data.warnings.map((w) => (
                      <li key={w}>• {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="mb-2 flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">
                    {data.containers.length} Container
                  </h3>
                  {foreign > 0 && (
                    <span className="text-xs text-slate-500">
                      davon {foreign} nicht über das PC ausgerollt
                    </span>
                  )}
                </div>

                {data.containers.length === 0 ? (
                  <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                    Keine Container auf diesem Host.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          {[
                            'Container',
                            'Status',
                            'Stack',
                            'Netzwerk',
                            'Ports',
                            'Herkunft',
                            'Aktion',
                          ].map(
                            (h) => (
                              <th
                                key={h}
                                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                              >
                                {h}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {data.containers.map((c) => (
                          <ContainerRow
                            key={c.id || c.name}
                            c={c}
                            serverId={serverId}
                            onDone={() => void refetch()}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-2 text-xs text-slate-400">
                  Start, Stopp und Neustart sind möglich — bewusst nichts, was Daten löscht
                  (kein Entfernen, keine Volumes). Nicht laufende Container stehen oben.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
